import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  KeyRound,
  Users as UsersIcon,
  ChevronRight,
  Gift,
  ShoppingBag,
  Map as MapIcon,
} from 'lucide-react';
import { useLab, getCourseTemplateForChild } from '../LabContext';
import { LabLayout, Card, MaxAvatar, AnitaAvatar } from '../LabUI';
import { computeDayStatus, getWeeksForCourse } from '../data/lessons';

// localStorage key used to remember which child the parent last opened.
// Read by the Drawer to make "Мой путь" / "Лавка Нимни" jump straight
// into that child. Cleared (or simply ignored) when the parent returns
// to the family overview.
export const PARENT_SELECTED_STUDENT_KEY = 'nimnyLab:parentSelectedStudentId:v1';

const setSelectedStudentId = (sid) => {
  try {
    if (sid) {
      window.localStorage.setItem(PARENT_SELECTED_STUDENT_KEY, sid);
    } else {
      window.localStorage.removeItem(PARENT_SELECTED_STUDENT_KEY);
    }
    window.dispatchEvent(new Event('nimnyLab:selectedStudentChanged'));
  } catch (_e) { /* localStorage unavailable */ }
};

// Same-origin fall-through resolver mirrors LabContext.
const resolveLabApi = (path) => {
  const configured = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === 'undefined') return `${configured || ''}${path}`;
  if (!configured) return path;
  try {
    const u = new URL(configured);
    if (u.origin === window.location.origin) return `${configured}${path}`;
  } catch (_e) { /* same-origin */ }
  return path;
};
const PARENT_API = resolveLabApi('/api/lab/parent');
const ADMIN_API = resolveLabApi('/api/lab/admin');

// ----- helpers ----------------------------------------------------------
const courseLabel = (template, language) => {
  if (template === 'anita') return language === 'ru' ? 'Курс Аниты' : "Anita's course";
  return language === 'ru' ? 'Курс Макса' : "Max's course";
};

// Tally completed days / total days across all weeks of a child's course.
const computeChildStats = (progress, weeks) => {
  let completed = 0;
  let total = 0;
  for (const w of weeks) {
    for (const d of w.days || []) {
      if (d.placeholder && d.id !== 'day-2') continue;
      total += 1;
      const s = computeDayStatus(progress, w.id, d.id, weeks);
      if (s === 'completed') completed += 1;
    }
  }
  return { completed, total };
};

// ----- main component ---------------------------------------------------
const ParentDashboard = () => {
  const { t, tx, user, language, childProgress, authLoading, refreshBackendStudent } = useLab();

  // Clear any previous selection on entering the family overview so the
  // Drawer "Мой путь" reverts to /lab/parent until the parent explicitly
  // opens a child again.
  useEffect(() => {
    setSelectedStudentId(null);
  }, []);

  const [students, setStudents] = useState(null); // null = loading, [] = empty, [...] = loaded
  const [pendingCount, setPendingCount] = useState(null);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user || user.role !== 'parent') return undefined;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${ADMIN_API}/students`, { credentials: 'include' });
        if (!r.ok) {
          if (alive) setStudents([]);
          return;
        }
        const body = await r.json();
        if (!alive) return;
        const list = Array.isArray(body?.students) ? body.students : [];
        const activeStudents = list.filter((s) => s && s.active !== false);
        setStudents(activeStudents);
        // Hydrate each child's progress + nim state from the backend so the
        // parent dashboard reflects what the child has actually completed
        // (without this, parent's local state.progress[childId] stays empty
        // and completed days appear unfinished, blocking bonus awards).
        for (const s of activeStudents) {
          if (s && s.studentId) {
            refreshBackendStudent(s.studentId).catch(() => { /* ignore */ });
          }
        }
      } catch (_e) {
        if (alive) setStudents([]);
      }
    })();
    (async () => {
      try {
        const r = await fetch(`${PARENT_API}/prize-requests`, { credentials: 'include' });
        if (!r.ok) return;
        const body = await r.json();
        if (!alive) return;
        const list = Array.isArray(body?.requests) ? body.requests : [];
        setPendingCount(list.filter((x) => x.status === 'pending').length);
      } catch (_e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [authLoading, user]);

  // Default catalog shortcut targets the first available child (e.g. max).
  const firstChildId = useMemo(() => {
    const list = Array.isArray(students) ? students : [];
    const first = list.find((s) => s && s.studentId);
    return first ? first.studentId : 'max';
  }, [students]);

  return (
    <LabLayout desktopWide>
      <section className="mt-2">
        <h1
          className="text-3xl text-[#f3d9a4]"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="parent-dash-title"
        >
          {t('parentDashTitle')}
        </h1>
        <p className="mt-1 text-amber-200/65 font-serif">
          {tx({
            ru: 'Все дети семьи и родительские инструменты.',
            en: "All children in your family and the parent tools."
          })}
        </p>
      </section>

      <div className="mt-5 lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        {/* Left column: family — all children */}
        <div className="lg:col-span-8">
          <Card className="p-4" data-testid="parent-family-card">
            <div className="flex items-center justify-between px-1 pb-3">
              <h2
                className="text-[#f3d9a4] text-lg"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {tx({ ru: 'Дети семьи', en: 'Children in your family' })}
              </h2>
              {Array.isArray(students) && (
                <span className="text-amber-200/55 text-xs font-sans" data-testid="parent-family-count">
                  {students.length}
                </span>
              )}
            </div>

            {students === null ? (
              <div className="px-2 py-6 text-amber-200/55 text-sm font-serif" data-testid="parent-family-loading">
                {tx({ ru: 'Загружаем семью…', en: 'Loading your family…' })}
              </div>
            ) : students.length === 0 ? (
              <div className="px-2 py-6 text-amber-200/65 text-sm font-serif" data-testid="parent-family-empty">
                <p>{tx({
                  ru: 'Пока нет ни одного детского аккаунта.',
                  en: 'There are no child accounts yet.'
                })}</p>
                <Link
                  to="/lab/parent/users"
                  className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-300/30 bg-amber-300/10 text-amber-100 text-sm font-sans hover:bg-amber-300/15"
                  data-testid="parent-family-empty-create"
                >
                  <UsersIcon className="w-4 h-4" />
                  {tx({ ru: 'Создать аккаунт ребёнка', en: 'Create a child account' })}
                </Link>
              </div>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="parent-family-grid">
                {students.map((s) => (
                  <ChildCard
                    key={s.id || s.studentId}
                    student={s}
                    user={user}
                    progress={childProgress(s.studentId)}
                    language={language}
                  />
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Right column (desktop): parent tools */}
        <div className="lg:col-span-4 lg:mt-0 mt-4 space-y-3">
          <Card className="p-3" data-testid="parent-tools-card">
            <ToolRow
              icon={UsersIcon}
              label={tx({ ru: 'Дети и аккаунты', en: 'Students & Accounts' })}
              to="/lab/parent/users"
              testid="parent-action-users"
            />
            <div className="h-2" />
            <ToolRow
              icon={KeyRound}
              label={tx({ ru: 'Изменить мой пароль', en: 'Change my password' })}
              to="/lab/parent/account"
              testid="parent-action-account"
            />
          </Card>

          <Link
            to="/lab/parent/prize-requests"
            className="block group"
            data-testid="parent-prize-shop-card"
          >
            <Card className="p-4 hover:bg-amber-300/[0.04] transition-colors">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-200">
                  <Gift className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[#f3d9a4] text-base"
                    style={{ fontFamily: 'var(--font-heading)' }}
                    data-testid="parent-prize-shop-card-title"
                  >
                    {tx({ ru: 'Запросы детей', en: "Children's requests" })}
                  </div>
                  <div className="text-amber-200/65 font-serif text-xs mt-0.5">
                    {tx({
                      ru: 'Подтвердить или отклонить запросы из Лавки Нимни',
                      en: 'Approve or reject Nimny Prize Shop requests'
                    })}
                  </div>
                </div>
                {pendingCount !== null && pendingCount > 0 && (
                  <span
                    className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-300/30 bg-amber-300/10 text-amber-200 text-xs font-sans"
                    data-testid="parent-prize-shop-card-pending-count"
                  >
                    {tx({
                      ru: `Новые: ${pendingCount}`,
                      en: `New: ${pendingCount}`
                    })}
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-amber-200/45" />
              </div>
            </Card>
          </Link>

          <Link
            to={`/lab/student/${firstChildId}/prize-shop`}
            className="block group"
            data-testid="parent-prize-shop-catalog-card"
          >
            <Card className="p-4 hover:bg-amber-300/[0.04] transition-colors">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-200">
                  <ShoppingBag className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[#f3d9a4] text-base"
                    style={{ fontFamily: 'var(--font-heading)' }}
                    data-testid="parent-prize-shop-catalog-card-title"
                  >
                    {tx({ ru: 'Лавка Нимни', en: 'Nimny Prize Shop' })}
                  </div>
                  <div className="text-amber-200/65 font-serif text-xs mt-0.5">
                    {tx({
                      ru: 'Каталог призов и желания детей',
                      en: "Catalog and children's wishlist"
                    })}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-amber-200/45" />
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </LabLayout>
  );
};

// ----- child card -------------------------------------------------------
const ChildCard = ({ student, user, progress, language }) => {
  const sid = student.studentId;
  const courseTemplate =
    student.courseTemplate || getCourseTemplateForChild(user, sid);
  const weeks = useMemo(() => getWeeksForCourse(courseTemplate), [courseTemplate]);
  const { completed, total } = useMemo(
    () => computeChildStats(progress, weeks),
    [progress, weeks]
  );
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isMax = sid === 'max';
  const isAnita = sid === 'anita';

  return (
    <li>
      <Link
        to={`/lab/student/${sid}/path`}
        onClick={() => setSelectedStudentId(sid)}
        className="block rounded-xl border border-amber-300/15 bg-[#0a0f26]/60 hover:bg-amber-300/[0.04] transition-colors p-4"
        data-testid={`parent-child-card-${sid}`}
      >
        <div className="flex items-center gap-3">
          {isMax ? (
            <MaxAvatar size="sm" alt={student.name || 'Max'} />
          ) : isAnita ? (
            <AnitaAvatar size="sm" alt={student.name || 'Anita'} />
          ) : (
            <div
              className="w-16 h-16 rounded-full bg-amber-300/8 border-2 border-amber-200/25 flex items-center justify-center text-amber-200/90 font-sans text-2xl"
              aria-hidden="true"
            >
              {(student.name?.[0] || sid?.[0] || '?').toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[#f5ebe0]/95 font-sans text-base truncate">
              {student.name || sid}
            </div>
            <div className="text-amber-200/60 text-xs font-serif">
              {courseLabel(courseTemplate, language)}
            </div>
            <div className="mt-2 text-amber-200/55 text-xs font-sans">
              {completed} / {total}
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-amber-300/10 overflow-hidden">
              <div
                className="h-full bg-amber-300/85"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1.5 text-amber-200/80 font-sans">
            <MapIcon className="w-3.5 h-3.5" />
            {language === 'ru' ? 'Открыть путь' : 'Open path'}
          </span>
          <ChevronRight className="w-4 h-4 text-amber-200/45" />
        </div>
      </Link>
    </li>
  );
};

// ----- shared row -------------------------------------------------------
const ToolRow = ({ icon: Icon, label, to, testid }) => (
  <Link
    to={to}
    className="flex items-center gap-3 px-3 py-3 rounded-xl border border-amber-300/15 bg-[#0a0f26]/60 hover:bg-amber-300/5 transition-colors"
    data-testid={testid}
  >
    <span className="w-8 h-8 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85">
      <Icon className="w-[16px] h-[16px]" />
    </span>
    <span className="text-[#f5ebe0]/90 font-sans text-sm flex-1">{label}</span>
    <ChevronRight className="w-4 h-4 text-amber-200/45" />
  </Link>
);

export default ParentDashboard;
